/**
 * Regras das telas de Tecnologia e de Marketing na Visão Geral (#6504): Victor,
 * Benevides, Maycon e Felipe (que vê as duas áreas numa tela só).
 *
 * Demandas = solicitações da Central (`tarefas.categoria = 'solicitacao'`).
 *   - No prazo: concluída até o prazo da prioridade (src/lib/prazoSolicitacao.ts:
 *     urgência 1 dia útil, alta 2, média 5, baixa sem prazo). A régua vale para todo o
 *     histórico, como no plano de bônus, e não o vencimento editado à mão.
 *   - Retrabalho: concluída no mês que já foi reaberta alguma vez (voltou de "concluído"
 *     no histórico de status).
 *   - Projetos no prazo: tarefa de projeto concluída até o vencimento dela.
 * Marketing: contato = lead de formulário + conversa iniciada no WhatsApp (Meta Ads).
 *
 *   node --experimental-strip-types --test src/lib/visaoGeral/__tests__/tecnologia.test.ts
 */
import { prazoDaSolicitacao } from '../prazoSolicitacao.ts';
import type { TarefaPrioridade } from '../../../types.ts';

export const META_DEMANDAS_NO_PRAZO = 0.9;
export const META_RETRABALHO = 0.03;
export const META_PROJETOS_NO_PRAZO = 0.9;
export const META_CUSTO_POR_CONTATO = 8.5;
export const META_CONTRATOS_MIDIA_TRIMESTRE = 25;

/** Dia de Brasília ('AAAA-MM-DD') de um instante. */
export function diaEmBrasilia(iso: string): string {
  const partes = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit', day: '2-digit' })
    .formatToParts(new Date(iso));
  const p = (t: string) => partes.find(x => x.type === t)?.value ?? '';
  return `${p('year')}-${p('month')}-${p('day')}`;
}

// ─────────────────────────── Demandas ───────────────────────────

export interface DemandaConcluida {
  id: number;
  responsavel_id: string | null;
  prioridade: string | null;
  created_at: string;
  data_conclusao: string;
  /** Vencimento gravado na tarefa (o de projeto é definido no planejamento). */
  vencimento: string | null;
  projeto_id: number | null;
  /** Voltou de "concluído" alguma vez. */
  reaberta: boolean;
}

export interface EntregasDoPeriodo {
  concluidas: number;
  /** Com prazo pela prioridade (baixa não tem). */
  comPrazo: number;
  noPrazo: number;
  taxaPrazo: number | null;
  reabertas: number;
  taxaRetrabalho: number | null;
  projetoComPrazo: number;
  projetoNoPrazo: number;
  taxaProjetos: number | null;
}

const PRIORIDADES = new Set(['urgencia', 'alta', 'media', 'baixa']);

/** Prazo da demanda pela prioridade; `null` para baixa ou prioridade desconhecida. */
export function prazoDaDemanda(d: Pick<DemandaConcluida, 'prioridade' | 'created_at'>): string | null {
  const p = String(d.prioridade ?? '');
  return PRIORIDADES.has(p) ? prazoDaSolicitacao(p as TarefaPrioridade, new Date(d.created_at)) : null;
}

/** Contas das demandas concluídas (já filtradas pelo período e pela pessoa, se for o caso). */
export function entregasDoPeriodo(demandas: readonly DemandaConcluida[]): EntregasDoPeriodo {
  let comPrazo = 0, noPrazo = 0, reabertas = 0, projetoComPrazo = 0, projetoNoPrazo = 0;
  for (const d of demandas) {
    const dia = diaEmBrasilia(d.data_conclusao);
    const prazo = prazoDaDemanda(d);
    if (prazo) { comPrazo++; if (dia <= prazo) noPrazo++; }
    if (d.reaberta) reabertas++;
    if (d.projeto_id != null && d.vencimento) {
      projetoComPrazo++;
      if (dia <= d.vencimento.slice(0, 10)) projetoNoPrazo++;
    }
  }
  const n = demandas.length;
  return {
    concluidas: n, comPrazo, noPrazo, taxaPrazo: comPrazo ? noPrazo / comPrazo : null,
    reabertas, taxaRetrabalho: n ? reabertas / n : null,
    projetoComPrazo, projetoNoPrazo, taxaProjetos: projetoComPrazo ? projetoNoPrazo / projetoComPrazo : null,
  };
}

/** Concluídas por semana do mês (1ª = dias 1–7, 2ª = 8–14...). */
export function entregasPorSemana(demandas: readonly Pick<DemandaConcluida, 'data_conclusao'>[]): { semana: string; qtd: number }[] {
  const semanas = [0, 0, 0, 0, 0];
  for (const d of demandas) {
    const dia = Number(diaEmBrasilia(d.data_conclusao).slice(8, 10));
    semanas[Math.min(4, Math.floor((dia - 1) / 7))]++;
  }
  const ultima = semanas[4] ? 5 : 4;
  return semanas.slice(0, ultima).map((qtd, i) => ({ semana: `Semana ${i + 1}`, qtd }));
}

// ─────────────────────────── Mídia (Meta Ads) ───────────────────────────

export interface MetricaDeMidia {
  date: string;
  campaign_name: string | null;
  spend: number | string | null;
  leads: number | null;
  conversations_started: number | null;
  ad_id?: string | null;
  ad_name?: string | null;
}

export interface Desempenho {
  gasto: number;
  leads: number;
  conversas: number;
  contatos: number;
  /** Gasto ÷ contatos; null sem contato. */
  custo: number | null;
}

const somar = (linhas: readonly MetricaDeMidia[]): Desempenho => {
  let gasto = 0, leads = 0, conversas = 0;
  for (const l of linhas) { gasto += Number(l.spend) || 0; leads += Number(l.leads) || 0; conversas += Number(l.conversations_started) || 0; }
  const contatos = leads + conversas;
  return { gasto, leads, conversas, contatos, custo: contatos ? gasto / contatos : null };
};

/** Total do período, com o custo por contato. */
export const desempenhoDaMidia = (linhas: readonly MetricaDeMidia[]): Desempenho => somar(linhas);

/** Por campanha, do maior gasto para o menor. Campanha sem gasto no período fica de fora. */
export function porCampanha(linhas: readonly MetricaDeMidia[]): (Desempenho & { campanha: string })[] {
  const grupos = new Map<string, MetricaDeMidia[]>();
  for (const l of linhas) {
    const nome = l.campaign_name || 'Sem nome';
    if (!grupos.has(nome)) grupos.set(nome, []);
    grupos.get(nome)!.push(l);
  }
  return [...grupos.entries()]
    .map(([campanha, ls]) => ({ campanha, ...somar(ls) }))
    .filter(c => c.gasto > 0)
    .sort((a, b) => b.gasto - a.gasto);
}

/**
 * Os anúncios com menor custo por contato no período. Anúncio com poucos contatos
 * (menos de `minimoContatos`) não entra: 1 contato barato por acaso não é criativo bom.
 */
export function melhoresCriativos(
  linhas: readonly MetricaDeMidia[],
  quantos = 3,
  minimoContatos = 10,
): (Desempenho & { adId: string; nome: string; campanha: string | null })[] {
  const grupos = new Map<string, MetricaDeMidia[]>();
  for (const l of linhas) {
    if (!l.ad_id) continue;
    if (!grupos.has(l.ad_id)) grupos.set(l.ad_id, []);
    grupos.get(l.ad_id)!.push(l);
  }
  return [...grupos.entries()]
    .map(([adId, ls]) => {
      const ultimo = [...ls].sort((a, b) => b.date.localeCompare(a.date))[0];
      return { adId, nome: ultimo.ad_name || adId, campanha: ultimo.campaign_name ?? null, ...somar(ls) };
    })
    .filter(a => a.contatos >= minimoContatos && a.custo != null)
    .sort((a, b) => a.custo! - b.custo!)
    .slice(0, quantos);
}

/** Trimestre civil de uma data: início e fim exclusivo ('AAAA-MM-DD') e o rótulo. */
export function trimestreDe(d: Date): { inicio: string; fimExclusivo: string; rotulo: string } {
  const t = Math.floor(d.getMonth() / 3);
  const ano = d.getFullYear();
  const iso = (a: number, m0: number) => `${a + Math.floor(m0 / 12)}-${String((m0 % 12) + 1).padStart(2, '0')}-01`;
  return { inicio: iso(ano, t * 3), fimExclusivo: iso(ano, t * 3 + 3), rotulo: `${t + 1}º tri/${ano}` };
}
